import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, UserPlus } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import NigerianPhoneInput, { formatPhoneForApi } from '../shared/NigerianPhoneInput';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.phone || formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);

    try {
      await api.signup({
        ...formData,
        phone: formatPhoneForApi(formData.phone)
      });
      toast.success('Signup successful! Please check your email to verify your account.');
      navigate(redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFBF9] flex flex-col">
      <Header />
      
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-[#E6DCD3] p-8" data-testid="signup-form-container">
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl text-[#3E2723] mb-2">Create Account</h1>
              <p className="text-[#786E66]">Join the Crowns by Preshy family</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                    placeholder="Your full name"
                    data-testid="signup-name-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                    placeholder="your.email@example.com"
                    data-testid="signup-email-input"
                  />
                </div>
              </div>

              <NigerianPhoneInput
                value={formData.phone}
                onChange={(digits) => setFormData({ ...formData, phone: digits })}
                required={true}
                testId="signup-phone-input"
              />

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#786E66]" />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="w-full pl-10 pr-4 py-3 border border-[#E6DCD3] rounded-lg focus:ring-2 focus:ring-[#3E2723] focus:border-[#3E2723] outline-none transition-all"
                    placeholder="Minimum 8 characters"
                    data-testid="signup-password-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full py-3 font-medium transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                data-testid="signup-submit-button"
              >
                {loading ? 'Creating account...' : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Create Account
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#786E66]">
                Already have an account?{' '}
                <Link to="/login" className="text-[#3E2723] font-medium hover:underline" data-testid="signup-login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignupPage;
